
import { supabase } from "@/integrations/supabase/client";
import { Game, GameSession, GameAnalytics, MathSkillProgress } from "@/types/games";

export const gameService = {
  // Fetch available games for a specific grade and subject
  async getGamesForGradeAndSubject(grade: string, subject?: string): Promise<Game[]> {
    let query = supabase
      .from('games')
      .select('*')
      .eq('is_active', true)
      .lte('grade_min', parseInt(grade))
      .gte('grade_max', parseInt(grade));
    
    if (subject) {
      const { data: subjectData } = await supabase
        .from('subjects')
        .select('id')
        .eq('name', subject)
        .single();
      
      if (subjectData) {
        query = query.eq('subject_id', subjectData.id);
      }
    }
    
    const { data, error } = await query.order('name');
    
    if (error) {
      console.error('Error fetching games:', error);
      throw error;
    }
    
    // Type assertion to ensure proper Game interface compliance.
    // Cast via unknown because game_config is typed as `Json` in the
    // generated Supabase types but our domain Game type narrows it.
    return ((data || []) as unknown) as Game[];
  },

  // Create a new game session
  async createGameSession(gameId: string, grade: string, subject: string, userId: string): Promise<GameSession> {
    const { data, error } = await supabase
      .from('game_sessions')
      .insert({
        user_id: userId,
        game_id: gameId,
        grade,
        subject,
        difficulty_level: 1,
        session_data: {}
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating game session:', error);
      throw error;
    }
    
    return data as GameSession;
  },

  // Update game session progress
  async updateGameSession(sessionId: string, updates: Partial<GameSession>): Promise<void> {
    const { error } = await supabase
      .from('game_sessions')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId);
    
    if (error) {
      console.error('Error updating game session:', error);
      throw error;
    }
  },

  // Record game analytics
  async recordGameAnalytics(analytics: Partial<GameAnalytics>): Promise<void> {
    // Ensure required fields are present
    const analyticsData = {
      game_session_id: analytics.game_session_id!,
      user_id: analytics.user_id!,
      game_id: analytics.game_id!,
      learning_objective: analytics.learning_objective!,
      skill_assessed: analytics.skill_assessed!,
      mastery_level: analytics.mastery_level || 0,
      error_patterns: analytics.error_patterns || [],
      time_on_concept: analytics.time_on_concept || 0,
      interaction_data: analytics.interaction_data || {}
    };

    const { error } = await supabase
      .from('game_analytics')
      .insert(analyticsData);
    
    if (error) {
      console.error('Error recording game analytics:', error);
      throw error;
    }
  },

  // Update math skill progress
  async updateMathSkillProgress(userId: string, mathConcept: string, grade: string, progress: Partial<MathSkillProgress>): Promise<void> {
    const { error } = await supabase
      .from('math_skill_progress')
      .upsert({
        user_id: userId,
        math_concept: mathConcept,
        grade,
        ...progress,
        last_practiced: new Date().toISOString()
      }, {
        onConflict: 'user_id,math_concept,grade'
      });
    
    if (error) {
      console.error('Error updating math skill progress:', error);
      throw error;
    }
  },

  // Get user's math skill progress
  async getMathSkillProgress(userId: string, grade?: string): Promise<MathSkillProgress[]> {
    let query = supabase
      .from('math_skill_progress')
      .select('*')
      .eq('user_id', userId);
    
    if (grade) {
      query = query.eq('grade', grade);
    }
    
    const { data, error } = await query.order('last_practiced', { ascending: false });
    
    if (error) {
      console.error('Error fetching math skill progress:', error);
      throw error;
    }
    
    return (data || []) as MathSkillProgress[];
  },

  // Get user's game sessions
  async getUserGameSessions(userId: string, limit: number = 10): Promise<GameSession[]> {
    const { data, error } = await supabase
      .from('game_sessions')
      .select(`
        *,
        games:game_id (
          name,
          description,
          game_type
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching user game sessions:', error);
      throw error;
    }
    
    return (data || []) as GameSession[];
  }
};
